
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { SIDEBAR_LINKS, SECONDARY_LINKS, SERVICE_LINKS } from '../../constants';
import { NavItem } from '../../types';
import { useAppSelector } from '../../store/hooks';
import { selectUserDisplayName, selectEmployee, selectUser, selectHasPermission, selectPermissions, selectToken } from '../../store/slices/authSlice';
import { getDSRPermissions, DSR_PENDING_CHANGED_EVENT } from '../../lib/dsr-helpers';
import { VersionsModal } from '../VersionsModal';
import { AppSwitcher } from './AppSwitcher';
import { Avatar } from './Avatar';
import { ChevronDown, Clock, ShieldCheck, Hash, Users, Wrench, Briefcase, ClipboardCheck, FileSpreadsheet, ListTodo } from 'lucide-react';
import { API_CONFIG } from '../../lib/api';
import { resolveHrmsMediaUrl, hrmsRBACClient } from '../../lib/hrms-rbac';


export const Sidebar: React.FC = () => {
  const location = useLocation();
  const [showChangelog, setShowChangelog] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const [appVersion, setAppVersion] = useState('v1.5.0');
  const [versionLoaded, setVersionLoaded] = useState(false);
  const userDisplayName = useAppSelector(selectUserDisplayName);
  const employee = useAppSelector(selectEmployee);
  const user = useAppSelector(selectUser);

  useEffect(() => {
    fetch(`${API_CONFIG.BASE_URL}/health`)
      .then(res => res.json())
      .then(data => {
        if (data.version) setAppVersion(`v${data.version}`);
      })
      .catch(() => {})
      .finally(() => {
        setVersionLoaded(true);
      });
  }, []);

  const changelogSeenKey = useMemo(
    () => `marketing_changelog_seen_${appVersion}_${user?.id ?? 'anon'}`,
    [appVersion, user?.id]
  );
  
  // Check each permission (codes must match HRMS; Domains tab uses rbac.view_domain_tab)
  const hasViewDomainTab = useAppSelector(selectHasPermission('rbac.view_domain_tab'));
  const hasViewDomain = useAppSelector(selectHasPermission('marketing.view_domain'));
  const hasViewContact = useAppSelector(selectHasPermission('marketing.view_contact'));
  const hasViewLead = useAppSelector(selectHasPermission('marketing.view_lead'));
  const hasViewCampaign = useAppSelector(selectHasPermission('marketing.view_campaign'));
  const hasViewCustomer = useAppSelector(selectHasPermission('marketing.view_customer'));

  const hasViewOrganization = useAppSelector(selectHasPermission('marketing.view_organization'));
  const hasViewReport = useAppSelector(selectHasPermission('marketing.view_report'));
  const hasViewMyTeam = useAppSelector(selectHasPermission('marketing.view_myteam'));
  const hasViewEvents = useAppSelector(selectHasPermission('marketing.view_events'));
  const hasViewService = useAppSelector(selectHasPermission('service.view'));
  const hasAdmin = useAppSelector(selectHasPermission('marketing.admin'));

  const onServiceRoute = location.pathname.startsWith('/service');
  // Admin only gets the blue highlight while you are on one of its pages (same rule as Service)
  const onAdminRoute = location.pathname.startsWith('/numbering-series') || location.pathname.startsWith('/roles');
  useEffect(() => {
    if (onServiceRoute) setServiceOpen(true);
  }, [onServiceRoute]);

  // HRMS Daily Service Reports — "Work & Approvals" group (docs/DSR_MODULE_INTEGRATION.md §9.1)
  const permissionCodes = useAppSelector(selectPermissions);
  const dsrPerms = useMemo(() => getDSRPermissions(permissionCodes, !!user?.is_superuser), [permissionCodes, user]);
  const canLogDsr = dsrPerms.canCreateIndoor || dsrPerms.canCreateOutdoor || dsrPerms.canCreateExpense;
  const canViewDsrHistory = canLogDsr || dsrPerms.canViewExpense || dsrPerms.canViewAllDSR
    || permissionCodes.includes('dsr.indoor_view') || permissionCodes.includes('dsr.outdoor_view');
  // "Pending for me" count badge on View DSR History (guide §9.1 sidebar badge / §3.7). Refreshes
  // every 5 min and right after an approve/reject elsewhere. 0 for non-approvers → no badge.
  const token = useAppSelector(selectToken);
  const [pendingForMe, setPendingForMe] = useState(0);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const refresh = () => hrmsRBACClient.getPendingApprovals(token)
      .then(p => { if (!cancelled) setPendingForMe(p.dsr.length + p.expense.length); })
      .catch(() => {});
    refresh();
    const timer = setInterval(refresh, 5 * 60 * 1000);
    window.addEventListener(DSR_PENDING_CHANGED_EVENT, refresh);
    return () => { cancelled = true; clearInterval(timer); window.removeEventListener(DSR_PENDING_CHANGED_EVENT, refresh); };
  }, [token]);
  const workLinks = [
    ...(canLogDsr ? [{ title: 'Log DSR', href: '/daily-service-reports/new', icon: ClipboardCheck, badge: 0 }] : []),
    // Approvers always get the link, even without view permissions, so they can reach their queue.
    ...(canViewDsrHistory || pendingForMe > 0 ? [{ title: 'View DSR History', href: pendingForMe > 0 ? '/daily-service-reports?tab=pending' : '/daily-service-reports', icon: FileSpreadsheet, badge: pendingForMe }] : []),
    // HRMS To-Do tasks (anyone can be assigned one) — its own page, as in HRMS.
    { title: 'My To-Do', href: '/my-todo', icon: ListTodo, badge: 0 },
  ];
  const onWorkRoute = location.pathname.startsWith('/daily-service-reports') || location.pathname.startsWith('/my-todo');
  useEffect(() => {
    if (onWorkRoute) setWorkOpen(true);
  }, [onWorkRoute]);

  // Filter links based on permissions
  const filteredSidebarLinks = useMemo(() => {
    return SIDEBAR_LINKS.filter(link => {
      if (!link.permission) return true;
      switch (link.permission) {
        case 'rbac.view_domain_tab': return hasViewDomainTab;
        case 'marketing.view_domain': return hasViewDomain;
        case 'marketing.view_contact': return hasViewContact;
        case 'marketing.view_lead': return hasViewLead;
        case 'marketing.view_campaign': return hasViewCampaign;
        case 'marketing.view_customer': return hasViewCustomer;
        case 'marketing.view_organization': return hasViewOrganization;
        case 'marketing.view_database': return hasViewOrganization || hasViewCustomer || hasViewContact;
        case 'marketing.view_report': return hasViewReport;
        case 'marketing.view_myteam': return hasViewMyTeam;
        case 'marketing.view_events': return hasViewEvents;
        case 'marketing.admin': return hasAdmin;
        default: return true;
      }
    });
  }, [hasViewDomainTab, hasViewDomain, hasViewContact, hasViewLead, hasViewCampaign, hasViewCustomer, hasViewOrganization, hasViewReport, hasViewMyTeam, hasViewEvents, hasAdmin]);

  // Filter secondary links (Settings and Support don't need permissions for now)
  const filteredSecondaryLinks = SECONDARY_LINKS;
  
  // Get designation or role for subtitle
  // Live clock for the profile card ("3:49 pm • 29 Sept")
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const clockLabel = `${now.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase()} • ${now.getDate()} ${now.toLocaleString('en-IN', { month: 'short' })}`;

  const getUserRole = () => {
    if (user?.is_superuser) {
      return 'Super Admin';
    }
    if (employee?.designation) {
      return employee.designation;
    }
    return 'User';
  };

  const closeChangelog = () => {
    try {
      localStorage.setItem(changelogSeenKey, '1');
    } catch {
      // ignore storage failures
    }
    setShowChangelog(false);
  };

  useEffect(() => {
    if (!user?.id || !versionLoaded) return;
    try {
      const seen = localStorage.getItem(changelogSeenKey);
      if (!seen) {
        setShowChangelog(true);
      }
    } catch {
      // ignore storage errors
    }
  }, [user?.id, versionLoaded, changelogSeenKey]);

  return (
    <aside className="w-60 h-screen bg-white border-r border-slate-200/60 flex flex-col fixed left-0 top-0 z-30">
      <div className="p-5 flex flex-col h-full">
        <div className="flex items-center gap-2.5 mb-7 px-2">
          <Link to="/" className="flex-shrink-0 hover:opacity-80 transition-all" aria-label="S&M Hub home">
            <img
              src="/aureole-logo.png"
              alt="Aureole"
              className="w-10 h-10 rounded object-contain"
            />
          </Link>
          <AppSwitcher currentName="S&M Hub" />
        </div>
        <div className="px-2 -mt-5 mb-5 flex items-center justify-between gap-2">
          <p className="text-[11px] font-medium text-slate-500">
            Powered by <span className="font-semibold text-slate-700">BeForth</span>
          </p>
          <button
            type="button"
            onClick={() => setShowChangelog(true)}
            className="text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5 hover:bg-blue-100 transition-colors"
            title="View changelog"
          >
            {appVersion}
          </button>
        </div>

        {/* flex-1 + min-h-0 + overflow: the menu scrolls when it's taller than the screen; logo and bottom section stay put */}
        <nav className="space-y-0.5 flex-1 min-h-0 overflow-y-auto sidebar-scroll -mx-1 px-1">
          <p className="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 mt-2">Main Menu</p>
          {filteredSidebarLinks.map((item) => (
            <SidebarItem key={item.title} item={item} />
          ))}

          {hasViewService && (
            <div>
              <button
                type="button"
                onClick={() => setServiceOpen(o => !o)}
                className={`w-full group flex items-center justify-between rounded-lg text-[13px] transition-all duration-200 font-medium px-3 py-2 ${
                  serviceOpen && onServiceRoute
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Wrench
                    size={18}
                    strokeWidth={serviceOpen && onServiceRoute ? 2.2 : 1.8}
                    className={serviceOpen && onServiceRoute ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}
                  />
                  <span className={serviceOpen && onServiceRoute ? 'font-semibold' : ''}>Service</span>
                </div>
                <ChevronDown
                  size={14}
                  className={`text-slate-400 transition-transform duration-200 ${serviceOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {serviceOpen && (
                <div className="mt-0.5 ml-3 pl-3 border-l-2 border-blue-100 space-y-0.5">
                  {SERVICE_LINKS.map((link) => (
                    <NavLink
                      key={link.href}
                      to={link.href}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 w-full rounded-lg text-[12.5px] transition-all duration-200 font-medium px-2.5 py-1.5 ${
                          isActive
                            ? 'bg-blue-50 text-blue-700 font-semibold'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <link.icon size={15} strokeWidth={isActive ? 2.2 : 1.8} className={isActive ? 'text-blue-500' : 'text-slate-400'} />
                          {link.title}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {workLinks.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setWorkOpen(o => !o)}
                className={`w-full group flex items-center justify-between rounded-lg text-[13px] transition-all duration-200 font-medium px-3 py-2 ${
                  workOpen && onWorkRoute
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Briefcase
                    size={18}
                    strokeWidth={workOpen && onWorkRoute ? 2.2 : 1.8}
                    className={workOpen && onWorkRoute ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}
                  />
                  <span className={workOpen && onWorkRoute ? 'font-semibold' : ''}>Work & Approvals</span>
                </div>
                <ChevronDown
                  size={14}
                  className={`text-slate-400 transition-transform duration-200 ${workOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {workOpen && (
                <div className="mt-0.5 ml-3 pl-3 border-l-2 border-blue-100 space-y-0.5">
                  {workLinks.map((link) => (
                    <NavLink
                      key={link.href}
                      to={link.href}
                      end
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 w-full rounded-lg text-[12.5px] transition-all duration-200 font-medium px-2.5 py-1.5 ${
                          isActive
                            ? 'bg-blue-50 text-blue-700 font-semibold'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <link.icon size={15} strokeWidth={isActive ? 2.2 : 1.8} className={isActive ? 'text-blue-500' : 'text-slate-400'} />
                          {link.title}
                          {link.badge > 0 && (
                            <span className="ml-auto inline-flex min-w-[1.25rem] justify-center rounded-full bg-orange-500 px-1.5 text-[10px] font-bold text-white" title={`${link.badge} waiting for your approval`}>
                              {link.badge}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="mt-auto pt-2 space-y-0 shrink-0 border-t border-slate-100">
          {/* ── Admin Section ── */}
          {hasAdmin && (
            <div className="mb-1">
              {/* Admin toggle button */}
              <button
                type="button"
                onClick={() => setAdminOpen(o => !o)}
                className={`w-full group flex items-center justify-between rounded-lg text-[13px] transition-all duration-200 font-medium px-3 py-1.5 ${
                  (adminOpen && onAdminRoute)
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-3">
                  <ShieldCheck
                    size={18}
                    strokeWidth={adminOpen && onAdminRoute ? 2.2 : 1.8}
                    className={adminOpen && onAdminRoute ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}
                  />
                  <span className={adminOpen && onAdminRoute ? 'font-semibold' : ''}>Administration</span>
                </div>
                <ChevronDown
                  size={14}
                  className={`text-slate-400 transition-transform duration-200 ${adminOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown items */}
              {adminOpen && (
                <div className="mt-0.5 ml-3 pl-3 border-l-2 border-blue-100 space-y-0.5">
                  <NavLink
                    to="/numbering-series"
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 w-full rounded-lg text-[12.5px] transition-all duration-200 font-medium px-2.5 py-1.5 ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 font-semibold'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Hash
                          size={15}
                          strokeWidth={isActive ? 2.2 : 1.8}
                          className={isActive ? 'text-blue-500' : 'text-slate-400'}
                        />
                        Numbering Series
                      </>
                    )}
                  </NavLink>

                  <NavLink
                    to="/roles"
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 w-full rounded-lg text-[12.5px] transition-all duration-200 font-medium px-2.5 py-1.5 ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 font-semibold'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Users
                          size={15}
                          strokeWidth={isActive ? 2.2 : 1.8}
                          className={isActive ? 'text-blue-500' : 'text-slate-400'}
                        />
                        Roles
                      </>
                    )}
                  </NavLink>

                </div>
              )}
            </div>
          )}

          {/* Secondary links (Settings / Support) side by side */}
          <div className="grid grid-cols-2 gap-1 mt-0.5">
            {filteredSecondaryLinks.map((item) => (
              <NavLink
                key={item.title}
                to={item.href}
                title={item.title}
                className={({ isActive }) =>
                  `group flex items-center gap-2 rounded-lg text-[12.5px] transition-colors font-medium px-2.5 py-1.5 ${
                    isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon size={16} strokeWidth={isActive ? 2.2 : 1.8} className={isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'} />
                    <span className="truncate">{item.title.split(' ')[0]}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <div className="mt-1 flex items-center gap-2.5 px-2 py-1.5 rounded-lg" title={`${userDisplayName} · ${clockLabel}`}>
            <Avatar
              src={resolveHrmsMediaUrl(employee?.profile_picture || user?.profile_picture)}
              name={userDisplayName}
              className="w-8 h-8 rounded-full bg-blue-100 border border-blue-200/50 text-blue-600 text-[11px] shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-slate-900 truncate leading-tight">{userDisplayName}</p>
              <p className="flex items-center gap-1.5 text-[11px] leading-tight text-slate-500 font-medium min-w-0">
                <span className="truncate text-blue-700">{getUserRole()}</span>
                <span className="text-slate-300">·</span>
                <span className="shrink-0 text-slate-400">{clockLabel.split(' • ')[0]}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <VersionsModal
        isOpen={showChangelog}
        onClose={closeChangelog}
      />
    </aside>
  );
};

const SidebarItem: React.FC<{ item: NavItem }> = ({ item }) => {
  return (
    <NavLink
      to={item.href}
      end={item.href === '/database' ? false : undefined}
      className={({ isActive }) => `
        group flex items-center justify-between w-full rounded-lg text-[13px] transition-all duration-200 font-medium px-3 py-2
        ${isActive
          ? 'bg-blue-50 text-blue-700'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
      `}
    >
      {({ isActive }) => (
        <>
          <div className="flex items-center gap-3">
            <item.icon
              size={18}
              strokeWidth={isActive ? 2.2 : 1.8}
              className={isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}
            />
            <span className={isActive ? 'font-semibold' : ''}>{item.title}</span>
          </div>
          {item.badge && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${isActive ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
};
